"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Loader2, Star, User } from "lucide-react";
import { DockSheet } from "@/components/ui/dock-sheet";
import { DialogCard } from "@/components/ui/dialog-card";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/hooks/use-mobile";
import { useProfileReviews } from "@/hooks/use-profile-reviews";
import { ReviewCard } from "@/components/reviews/review-card";
import { StarRating } from "@/components/reviews/star-rating";
import { cn } from "@/lib/utils";
import type { ProfileReviewRole, ReceivedReviewRole } from "@/lib/reviews/schema";

// Role filter tabs. Presentational — the active role is owned by the dialog so
// the tabs can live in the header (flush with the title) instead of the
// scroll area. See ProfileReviewsDialog.
//
// No `dark` prop: the one call site (ProfileReviewsDialog, both breakpoints)
// passed it unconditionally, so the page branch was dead and is gone.
// @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md
export function ReviewRoleTabs({
  roles,
  activeRole,
  onChange,
  className,
}: {
  roles: ProfileReviewRole[];
  activeRole: ProfileReviewRole;
  onChange: (role: ProfileReviewRole) => void;
  className?: string;
}) {
  const t = useTranslations("reviews");
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {roles.map((role) => {
        const selected = activeRole === role;
        return (
          <button
            key={role}
            type="button"
            onClick={() => onChange(role)}
            className={cn(
              "rounded-full px-4 py-2 text-sm font-semibold transition-colors",
              selected
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-foreground",
            )}
          >
            {t(`titleAs.${role}`)}
          </button>
        );
      })}
    </div>
  );
}

type ProfileReviewsPanelProps = {
  profileId: string;
  role: ProfileReviewRole;
  enabled: boolean;
};

// Same story as ReviewRoleTabs above: the former `dark` prop had exactly one
// call site and it was always `true`. Collapsed into the on-panel branch.
export function ProfileReviewsPanel({
  profileId,
  role,
  enabled,
}: ProfileReviewsPanelProps) {
  const t = useTranslations("reviews");
  const effectiveRole = role;
  const isGivenTab = effectiveRole === "given";
  const { state, loadingMore, fetchNextPage } = useProfileReviews(
    enabled ? profileId : null,
    effectiveRole,
    enabled,
  );

  const stats = state.kind === "success" ? state.stats : null;
  const reviews = state.kind === "success" ? state.reviews : [];
  const hasMore = state.kind === "success" ? state.hasMore : false;
  const totalCount =
    state.kind === "success"
      ? state.totalCount
      : effectiveRole === "seller"
        ? stats?.as_seller_count ?? 0
        : effectiveRole === "buyer"
          ? stats?.as_buyer_count ?? 0
          : 0;

  const topBadges = useMemo(() => {
    if (effectiveRole === "given") return [];
    const counts =
      effectiveRole === "seller"
        ? stats?.as_seller_badge_counts ?? {}
        : stats?.as_buyer_badge_counts ?? {};
    return Object.entries(counts)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .slice(0, 5);
  }, [effectiveRole, stats]);

  // "Given" reviews are scores the profile handed out, not its own reputation —
  // an average/star summary there would be misleading, so only received tabs show it.
  const avgScore = isGivenTab
    ? null
    : effectiveRole === "seller"
      ? stats?.as_seller_avg ?? null
      : stats?.as_buyer_avg ?? null;
  const reviewCount = totalCount;

  return (
    <div className="space-y-4">
      {state.kind === "loading" ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-foreground" />
        </div>
      ) : state.kind === "error" ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t("profileLoadError")}
        </p>
      ) : (
        <>
          {isGivenTab ? (
            reviewCount > 0 ? (
              <p
                className={cn(
                  "py-4 text-center text-sm",
                  "text-muted-foreground",
                )}
              >
                {t("profileCount", { count: reviewCount })}
              </p>
            ) : null
          ) : (
            <div className="flex flex-col items-center gap-1 py-4 text-center">
              <p className="text-4xl font-bold tabular-nums text-foreground">
                {typeof avgScore === "number" ? avgScore.toFixed(1) : "—"}
              </p>
              <StarRating value={avgScore ?? 0} readOnly size={22} />
              {reviewCount > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("profileCount", { count: reviewCount })}
                </p>
              ) : null}
            </div>
          )}

          {topBadges.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-base font-semibold text-foreground">{t("topBadges")}</h3>
              <div className="flex flex-wrap gap-2">
                {topBadges.map(([code, count]) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-sm font-semibold text-secondary-foreground"
                  >
                    {t(`badges.${code}`)}
                    <span className="text-muted-foreground">
                      ({count as number})
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {reviews.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("profileEmpty")}
            </p>
          ) : (
            <div key={effectiveRole} className="space-y-3">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          {hasMore ? (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="secondary"
                className="bg-muted text-foreground hover:bg-sheet-handle"
                disabled={loadingMore}
                onClick={fetchNextPage}
              >
                {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type ProfileReviewsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileId: string;
  initialRole?: ReceivedReviewRole;
  showGiven?: boolean;
};

export function ProfileReviewsDialog({
  open,
  onOpenChange,
  profileId,
  initialRole = "seller",
  showGiven = false,
}: ProfileReviewsDialogProps) {
  const isMobile = useMobile();
  const t = useTranslations("reviews");
  const title = t("profileTitle");

  const [activeRole, setActiveRole] = useState<ProfileReviewRole>(initialRole);
  // Clamp away the given tab if it's been hidden while it was active.
  const effectiveRole: ProfileReviewRole =
    activeRole === "given" && !showGiven ? "seller" : activeRole;
  const tabRoles: ProfileReviewRole[] = showGiven
    ? ["seller", "buyer", "given"]
    : ["seller", "buyer"];

  useEffect(() => {
    if (open) setActiveRole(initialRole);
  }, [open, initialRole, profileId]);

  const tabs = (
    <ReviewRoleTabs
      roles={tabRoles}
      activeRole={effectiveRole}
      onChange={setActiveRole}
    />
  );

  const panel = (
    <ProfileReviewsPanel profileId={profileId} role={effectiveRole} enabled={open} />
  );

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        belowTitle={tabs}
        zIndex={10050}
        contentClassName="pt-1 overflow-x-hidden"
      >
        <div className="flex flex-col gap-4">{panel}</div>
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      belowTitle={tabs}
      panelClassName="max-w-lg max-h-[min(85dvh,720px)]"
    >
      {panel}
    </DialogCard>
  );
}

export function ProfileReviewsMenuIcon() {
  return <Star className="mr-2 h-4 w-4" />;
}

export function ReviewCounterpartyAvatar({
  url,
  name,
  className,
}: {
  url?: string | null;
  name: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted ring-1 ring-border/60",
        className,
      )}
    >
      {url ? (
        <Image src={url} alt={name} fill className="object-cover" sizes="40px" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <User className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

export type ReviewShellProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
};

export function ReviewResponsiveShell({
  open,
  onOpenChange,
  title,
  children,
  footer,
}: ReviewShellProps) {
  const isMobile = useMobile();

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        footer={footer}
        zIndex={10050}
        contentClassName="pt-1 overflow-x-hidden"
      >
        <div className="flex flex-col gap-4">{children}</div>
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      panelClassName="max-w-md h-[min(90dvh,720px)] max-h-[min(90dvh,720px)]"
      footer={footer}
    >
      <div className="flex flex-col gap-4">{children}</div>
    </DialogCard>
  );
}
